import React, { useState } from 'react';
import { Download, X, Smartphone, Wifi, WifiOff, Bell, BellOff, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { usePWAInstall, useOnlineStatus, usePushNotifications } from '../hooks/usePWA';

const PWAInstallBanner = () => {
  const { isInstallable, isInstalled, install } = usePWAInstall();
  const isOnline = useOnlineStatus();
  const { permission, requestPermission } = usePushNotifications();
  const [dismissed, setDismissed] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  // Listen for service worker updates
  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setShowUpdateBanner(true);
              }
            });
          }
        });
      });
    }
  }, []);

  const handleInstall = async () => {
    const success = await install();
    if (success) {
      setDismissed(true);
    }
  };

  const handleEnableNotifications = async () => {
    await requestPermission();
  };

  const handleUpdate = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
    window.location.reload();
  };

  // Update available banner
  if (showUpdateBanner) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50" data-testid="pwa-update-banner">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-2xl p-4 border border-blue-400/20">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <RefreshCw className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Update Available</h3>
              <p className="text-xs text-blue-100 mt-1">
                A new version of HRMS is ready. Refresh to get the latest features.
              </p>
            </div>
            <button
              onClick={() => setShowUpdateBanner(false)}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="dismiss-update-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleUpdate}
              size="sm"
              className="flex-1 bg-white text-blue-600 hover:bg-blue-50"
              data-testid="refresh-app-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Now
            </Button>
            <Button
              onClick={() => setShowUpdateBanner(false)}
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10"
            >
              Later
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Install prompt banner
  if (isInstallable && !dismissed && !isInstalled) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50" data-testid="pwa-install-banner">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl shadow-2xl p-4 border border-emerald-400/20">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Install HRMS App</h3>
              <p className="text-xs text-emerald-100 mt-1">
                Add to your home screen for quick access, offline support, and push notifications.
              </p>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="dismiss-install-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleInstall}
              size="sm"
              className="flex-1 bg-white text-emerald-600 hover:bg-emerald-50"
              data-testid="install-app-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Install
            </Button>
            <Button
              onClick={() => setDismissed(true)}
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10"
            >
              Not Now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Offline indicator component
export const OfflineIndicator = () => {
  const isOnline = useOnlineStatus();

  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2" data-testid="offline-indicator">
      <WifiOff className="w-4 h-4" />
      <span>You&apos;re offline. Some features may be limited.</span>
    </div>
  );
};

// Notification permission prompt
export const NotificationPrompt = ({ onClose }) => {
  const { permission, requestPermission } = usePushNotifications();
  const [dismissed, setDismissed] = useState(false);

  const handleEnable = async () => {
    await requestPermission();
    setDismissed(true);
    if (onClose) onClose();
  };

  if (permission !== 'default' || dismissed) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50" data-testid="notification-prompt">
      <div className="bg-slate-800 text-white rounded-xl shadow-2xl p-4 border border-slate-700">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Bell className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-sm">Enable Notifications</h3>
            <p className="text-xs text-slate-400 mt-1">
              Get notified about leave approvals, salary updates, and important announcements.
            </p>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            onClick={handleEnable}
            size="sm"
            className="flex-1 bg-blue-500 hover:bg-blue-600"
            data-testid="enable-notifications-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            Enable
          </Button>
          <Button
            onClick={() => setDismissed(true)}
            size="sm"
            variant="ghost"
            className="text-slate-300 hover:bg-slate-700"
          >
            Skip
          </Button>
        </div>
      </div>
    </div>
  );
};

// Online status badge
export const OnlineStatusBadge = () => {
  const isOnline = useOnlineStatus();

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
      isOnline 
        ? 'bg-emerald-100 text-emerald-700' 
        : 'bg-amber-100 text-amber-700'
    }`} data-testid="online-status-badge">
      {isOnline ? (
        <>
          <Wifi className="w-3 h-3" />
          <span>Online</span>
        </>
      ) : (
        <>
          <WifiOff className="w-3 h-3" />
          <span>Offline</span>
        </>
      )}
    </div>
  );
};

export default PWAInstallBanner;
